import getBrowserTimezone from "./getBrowserTimezone.js";

function buildConfigUrlWithTimezone(configUrl) {
  const timezone = getBrowserTimezone();

  try {
    const url = new URL(
      configUrl,
      typeof window !== "undefined" ? window.location.href : "https://localhost"
    );
    url.searchParams.set("timezone", timezone);
    return url.toString();
  } catch {
    const separator = configUrl.includes("?") ? "&" : "?";
    return `${configUrl}${separator}timezone=${encodeURIComponent(timezone)}`;
  }
}

function normalizeEventName(data) {
  if (!data || typeof data !== "object") {
    return null;
  }

  return data.event || data.type || data.name || null;
}

function normalizeEventData(rawData) {
  if (!rawData) {
    return null;
  }

  if (typeof rawData === "string") {
    try {
      return JSON.parse(rawData);
    } catch {
      return null;
    }
  }

  if (typeof rawData === "object") {
    return rawData;
  }

  return null;
}

export default class VitrinaTVPlayer {
  constructor(containerId, configUrl, vlight, opts = {}) {
    this.containerId = containerId;
    this.configUrl = buildConfigUrlWithTimezone(configUrl);
    this.vlight = vlight;
    this.opts = opts;
    this.handlers = new Map();
    this.destroyed = false;
    this.stubMode =
      typeof window === "undefined" ||
      typeof window.VitrinaTVSDK === "undefined";
    this.player = null;
    this._playerWindow = null;
    this._stubTimerId = null;
    this._onMessage = this._onMessage.bind(this);
    this._listenerAdded = false;
  }

  _emit(event, payload = {}) {
    const handlers = this.handlers.get(event) || [];
    const allHandlers = this.handlers.get("__all__") || [];

    handlers.forEach((handler) => {
      handler(payload);
    });

    allHandlers.forEach((handler) => {
      handler({ event, payload });
    });
  }

  _onMessage(event) {
    if (this.destroyed) {
      return;
    }

    const container = document.getElementById(this.containerId);
    const iframe = container?.querySelector("iframe");
    const nextPlayerWindow = iframe?.contentWindow || null;
    if (nextPlayerWindow && this._playerWindow !== nextPlayerWindow) {
      this._playerWindow = nextPlayerWindow;
    }

    if (!this._playerWindow || event.source !== this._playerWindow) {
      return;
    }

    const eventData = normalizeEventData(event.data);
    const eventName = normalizeEventName(eventData);

    if (!eventName) {
      return;
    }

    const payload =
      eventData && typeof eventData === "object" && "data" in eventData
        ? eventData.data
        : eventData;

    this._emit(eventName, payload);
  }

  _renderStub(container) {
    container.innerHTML = "";

    const stub = document.createElement("div");
    stub.setAttribute("data-vitrina-tv-stub", "true");
    stub.style.display = "flex";
    stub.style.alignItems = "center";
    stub.style.justifyContent = "center";
    stub.style.width = "100%";
    stub.style.height = "100%";
    stub.style.minHeight = "200px";
    stub.style.backgroundColor = "#000";
    stub.style.color = "#fff";
    stub.style.fontSize = "14px";
    stub.style.textAlign = "center";
    stub.style.padding = "16px";
    stub.textContent = "Vitrina TV player is not available";

    container.appendChild(stub);

    this._stubTimerId = window.setTimeout(() => {
      if (!this.destroyed) {
        this._emit("playerinitend", {});
      }
    }, 100);
  }

  createPlayer() {
    if (this.destroyed || typeof document === "undefined") {
      return;
    }

    if (this._listenerAdded || this.player) {
      return;
    }

    const container = document.getElementById(this.containerId);

    if (!container) {
      return;
    }

    if (this.stubMode) {
      this._renderStub(container);
      return;
    }

    const SDK = window.VitrinaTVSDK;
    const useControls = this.opts.useControls ?? false;

    window.addEventListener("message", this._onMessage);
    this._listenerAdded = true;

    if (typeof SDK === "function") {
      this.player = new SDK(this.containerId, this.configUrl, this.vlight, {
        useControls,
      });

      if (typeof this.player.createPlayer === "function") {
        this.player.createPlayer();
      }
      return;
    }

    if (SDK && typeof SDK.createPlayer === "function") {
      this.player = SDK.createPlayer({
        container,
        containerId: this.containerId,
        configUrl: this.configUrl,
        vlight: this.vlight,
        useControls,
      });
      return;
    }
  }

  sendMessage(name, data) {
    if (this.destroyed) {
      return;
    }

    if (this.stubMode) {
      if (name === "getallprogrammes") {
        this._emit("allprogrammes", { programmes: [] });
      }

      return;
    }

    if (this.player?.sendMessage) {
      this.player.sendMessage(name, data);
      return;
    }

    if (this.player?.postMessage) {
      this.player.postMessage(name, data);
    }
  }

  on(event, handler) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }

    this.handlers.get(event).push(handler);

    return () => {
      const handlers = this.handlers.get(event) || [];
      this.handlers.set(
        event,
        handlers.filter((item) => item !== handler)
      );
    };
  }

  destroy() {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this._listenerAdded = false;

    if (this._stubTimerId) {
      window.clearTimeout(this._stubTimerId);
      this._stubTimerId = null;
    }

    if (!this.stubMode) {
      window.removeEventListener("message", this._onMessage);
    }

    if (this.player?.destroy) {
      this.player.destroy();
    }

    this.player = null;
    this._playerWindow = null;
    this.handlers.clear();

    const container = document.getElementById(this.containerId);

    if (container) {
      container.innerHTML = "";
    }
  }
}
